import { Link, LinkProps } from 'react-router';
import { isClientRoutablePath } from '~/lib/routing/route-registry';
import { cva, cx } from '~/styled-system/css';
import { focusVisibleRing } from './app-shell/shared';
import { Text } from '@understory-io/leaf/text';
import { ComponentProps, JSX } from 'react';

type TextLinkVisualProps = {
  /** Content color token (e.g. `'primary'`, `'secondary'`, `'tertiary'`).
   *  Maps to Leaf's `<Text color>` prop. Defaults to `'primary'`. */
  color?: ComponentProps<typeof Text>['color'];
  /** Text-style token (e.g. `'body-large-regular'`, `'caption'`). Defaults
   *  to `'body-large-regular'`. */
  style?: ComponentProps<typeof Text>['style'];
  /** Whether the text is underlined. Defaults to `true`. */
  underline?: boolean;
};

type TextRouterLinkProps = Omit<LinkProps, 'color' | 'style'> &
  TextLinkVisualProps;

export function TextLink({
  children,
  color = 'primary',
  style = 'body-large-regular',
  underline = true,
  className,
  to,
  reloadDocument,
  ...props
}: TextRouterLinkProps) {
  // Unlike Leaf's link adapter, react-router's `<Link>` will happily
  // client-navigate to a path this app doesn't serve — the legacy `apps/web`
  // pages sharing the domain (`/request`, `/upcoming-events`) then render our
  // 404 instead of being handed over.
  const documentLoad =
    reloadDocument ?? (typeof to === 'string' && !isClientRoutablePath(to));

  return (
    <Text
      as={Link}
      to={to}
      reloadDocument={documentLoad}
      style={style}
      color={color}
      className={cx(focusVisibleRing, linkStyles({ underline }), className)}
      {...props}
    >
      {children}
    </Text>
  );
}

type TextLinkButtonProps = Omit<
  JSX.IntrinsicElements['button'],
  'color' | 'style'
> &
  TextLinkVisualProps;

export function TextLinkButton({
  children,
  color = 'primary',
  style = 'body-large-regular',
  underline = true,
  className,
  ...props
}: TextLinkButtonProps) {
  return (
    <Text
      as="button"
      type="button"
      style={style}
      color={color}
      className={cx(focusVisibleRing, linkStyles({ underline }), className)}
      {...props}
    >
      {children}
    </Text>
  );
}

const linkStyles = cva({
  base: {
    cursor: 'interactive',
    borderRadius: 'tiny',
  },
  variants: {
    underline: {
      true: { textDecoration: 'underline' },
      false: { textDecoration: 'none' },
    },
  },
});
